import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  DataTable,
  Skeleton,
  EmptyState,
  Alert,
  DatePicker,
  KpiCard,
  Button,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import {
  useFeedback,
  useFeedbackStats,
  useMarkFeedbackRead,
} from '@/features/communications';
import type { FeedbackEntry } from '@/features/communications';

const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
];

function StarDisplay({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < rating ? 'text-amber-400' : 'text-neutral-200 dark:text-neutral-600'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

type Row = FeedbackEntry;

function FeedbackContent() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const rating = searchParams.get('rating') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';

  const { data, isLoading, isFetching, error } = useFeedback({
    page,
    rating: rating ? Number(rating) : '',
    from: from || undefined,
    to: to || undefined,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const { data: stats, isLoading: statsLoading } = useFeedbackStats();
  const markRead = useMarkFeedbackRead();

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function handleDateRangeChange(range: DateRange) {
    const params = new URLSearchParams(searchParams);
    params.set('from', range.from.toISOString().split('T')[0]);
    params.set('to', range.to.toISOString().split('T')[0]);
    params.set('page', '1');
    setSearchParams(params);
  }

  const dateRangeValue = useMemo(() => {
    if (from && to) return { from: new Date(from), to: new Date(to) };
    return undefined;
  }, [from, to]);

  const hasFilters = Boolean(rating || from || to);

  function clearFilters() {
    const params = new URLSearchParams(searchParams);
    params.delete('rating');
    params.delete('from');
    params.delete('to');
    params.set('page', '1');
    setSearchParams(params);
  }

  const columns: ColumnDef<Row>[] = useMemo(() => [
    {
      key: 'created_at',
      header: 'Date',
      render: (_v, row) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
          {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (_v, row) => (
        <div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {row.customer_name || 'Anonymous'}
          </p>
          {row.customer_phone ? (
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
              {row.customer_phone}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (_v, row) => (
        <div className="flex items-center gap-2">
          <StarDisplay rating={row.rating} />
          <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
            row.rating >= 4 ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300' :
            row.rating === 3 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
            'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300'
          }`}>
            {row.rating}
          </span>
        </div>
      ),
    },
    {
      key: 'comment',
      header: 'Comment',
      render: (_v, row) => (
        <p className="text-sm text-neutral-700 dark:text-neutral-200 line-clamp-2 max-w-[250px]">
          {row.comment || '—'}
        </p>
      ),
    },
    {
      key: 'bill_number',
      header: 'Bill #',
      render: (_v, row) => (
        <span className="text-xs font-mono tabular-nums text-neutral-700 dark:text-neutral-200">
          {row.bill_number || '—'}
        </span>
      ),
    },
    {
      key: 'cashier_name',
      header: 'Cashier',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-200">
          {row.cashier_name || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => {
        if (row.rating > 2) return null;
        if (row.is_read) {
          return (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success-600 dark:text-success-400">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Read
            </span>
          );
        }
        const pending = markRead.isPending && markRead.variables === row.id;
        return (
          <Button variant="secondary" size="sm" disabled={pending} onClick={() => markRead.mutate(row.id)}>
            {pending ? 'Marking…' : 'Mark read'}
          </Button>
        );
      },
    },
  ], [markRead]);

  const hasNegativeAlerts = (stats?.alerts_count ?? 0) > 0;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-400 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Customer Voice
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Feedback</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Customer feedback from &ldquo;Rate Us&rdquo; links in digital receipts. Monitor satisfaction and act on negative feedback.
          </p>
        </div>
      </div>

      {/* Negative feedback alert */}
      {hasNegativeAlerts ? (
        <Alert variant="danger" title={`${stats!.alerts_count} unread negative feedback`} description="1-2 star reviews received. Review and respond to maintain customer satisfaction." className="mb-6" />
      ) : null}

      {/* Stat Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={90} className="rounded-xl" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title="Avg Rating"
            value={stats.avg_rating > 0 ? stats.avg_rating.toFixed(1) : 'N/A'}
            icon={
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            }
            className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Total Reviews"
            value={stats.total_reviews.toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
            className="bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Positive %"
            value={`${stats.positive_pct}%`}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            className="bg-gradient-to-br from-success-50 to-emerald-50 dark:from-success-900/20 dark:to-emerald-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Alerts"
            value={stats.alerts_count.toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            className="bg-gradient-to-br from-danger-50 to-red-50 dark:from-danger-900/20 dark:to-red-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
        </div>
      ) : null}

      {/* Filters */}
      {/* relative z-30: lift this card's stacking context above the sibling
          cards below (which use backdrop-blur and would otherwise paint over
          the open dropdowns, washing them out). */}
      <div className="relative z-30 rounded-xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-md p-4 sm:p-5 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="w-full lg:w-40">
            <Select
              label="Rating"
              options={RATING_OPTIONS}
              value={rating}
              onChange={(v) => setParam('rating', typeof v === 'string' ? v : '')}
              placeholder="All Ratings"
            />
          </div>
          <div className="w-full lg:w-64">
            <DatePicker
              variant="range"
              label="Date Range"
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              maxDate={new Date()}
              placeholder="Select date range"
            />
          </div>
        </div>
      </div>

      {/* Data */}
      {error ? (
        <Alert
          variant="danger"
          title="Couldn't load feedback"
          description={error instanceof Error ? error.message : 'Please adjust the filters and try again.'}
          action={hasFilters ? { label: 'Clear filters', onClick: clearFilters } : undefined}
          className="mb-6"
        />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title={hasFilters ? 'No feedback matches these filters' : 'No feedback received'}
            description={
              hasFilters
                ? 'Try a wider date range or a different rating to see more results.'
                : 'Customer feedback will appear here when submitted via receipt links.'
            }
            action={hasFilters ? { label: 'Clear filters', onClick: clearFilters } : undefined}
            icon={
              <svg className="h-20 w-20 text-amber-200 dark:text-amber-800" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            }
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <section className="hidden sm:block rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Customer feedback">
            <DataTable<Row>
              columns={columns}
              data={rows}
              loading={isFetching}
              totalCount={data?.meta.total}
              currentPage={page}
              pageSize={20}
              onPageChange={(p) => setParam('page', String(p))}
              stickyHeader
            />
          </section>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3 animate-slide-up">
            {rows.map((row) => (
              <div
                key={row.id}
                className={`rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 ${
                  row.rating <= 2 && !row.is_read ? 'border-l-4 border-danger-500' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <StarDisplay rating={row.rating} size="lg" />
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
                    {new Date(row.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  </span>
                </div>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {row.customer_name || 'Anonymous'}
                </p>
                {row.comment ? (
                  <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-200 line-clamp-3">{row.comment}</p>
                ) : null}
                <div className="mt-2 flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                  {row.bill_number ? <span className="font-mono tabular-nums">#{row.bill_number}</span> : null}
                  {row.cashier_name ? <span>{row.cashier_name}</span> : null}
                </div>
                {row.rating <= 2 ? (
                  <div className="mt-3">
                    {row.is_read ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-600 dark:text-success-400">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Read
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={markRead.isPending && markRead.variables === row.id}
                        onClick={() => markRead.mutate(row.id)}
                      >
                        {markRead.isPending && markRead.variables === row.id ? 'Marking…' : 'Mark read'}
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-neutral-500 font-mono tabular-nums">
                Page {page} of {data?.meta.totalPages ?? 1}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= (data?.meta.totalPages ?? 1)}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function FeedbackViewerPage() {
  return (
    <PageContainer
      title="Feedback"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Feedback' },
      ]}
    >
      <ErrorBoundary resetKey="comm-feedback">
        <FeedbackContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
